'use client';

import { Box } from '@wishket/design-system';
import { useMemo, useState, PropsWithChildren } from 'react';
import { usePathname, useSelectedLayoutSegments } from 'next/navigation';

import UIOverlayContext, { DrawerType, ModalType } from './UIOverlayContext';

import { PATH } from '@/shared/constants';
import { useProfile } from '@/shared/hooks';
import { SearchDrawer } from '@/widgets/Search';
import { UserMenuDrawer } from '@/features/AppBar';
import OnBoardingModal from '@/widgets/OnBoardingModal';
import ProfileImageChangeModal from '@/widgets/Profile/ProfileImageChange';
import ProfileEditModal from '@/widgets/Profile/ProfileEdit';
import CollectionCreateModal from '@/widgets/CollectionModal/CollectionCreateModal';

const DEFAULT_PORTAL_ROOT_ID = 'portal';

const UIOverlayProvider = ({ children }: PropsWithChildren) => {
  const [articleId, setArticleId] = useState<number>();
  const [isOpen, setIsOpen] = useState<{
    [key in ModalType | DrawerType]: boolean;
  }>({
    userMenu: false,
    search: false,
    profile: false,
    profileImageChange: false,
    collectionCreate: false,
  });

  const { profile } = useProfile();
  const pathname = usePathname();
  const segments = useSelectedLayoutSegments();

  const isAuthorizedPage = useMemo(() => {
    return segments.includes('(authorized)');
  }, [segments]);
  const isSignupVerifyPage = pathname === PATH.SIGNUP_VERIFY;
  const nextPath = isAuthorizedPage ? PATH.HOME : pathname;

  const authNextParams = pathname.startsWith('/magazine')
    ? pathname
    : PATH.HOME;

  const handleOpen = (type: ModalType | DrawerType, articleId?: number) => {
    if (isSignupVerifyPage)
      return alert('이메일 인증 후 요즘IT 서비스 이용이 가능합니다.');

    if (type === 'collectionCreate' && articleId) {
      setArticleId(articleId);
    }

    setIsOpen((prev) => ({
      ...prev,
      [type]: true,
    }));
  };

  const handleClose = (type: ModalType | DrawerType) => {
    if (type === 'collectionCreate') {
      setArticleId(undefined);
    }

    setIsOpen((prev) => ({
      ...prev,
      [type]: false,
    }));
  };

  return (
    <UIOverlayContext.Provider
      value={{ handleOpen, handleClose, isOpen, isAuthorizedPage }}
    >
      {children}
      <SearchDrawer
        isOpen={isOpen.search}
        onClose={() => handleClose('search')}
      />
      <Box className="desktop:hidden">
        <UserMenuDrawer
          isOpen={isOpen.userMenu}
          onClose={() => handleClose('userMenu')}
          profile={profile}
          nextPath={nextPath}
          authNextParams={authNextParams}
          openProfileModal={() => handleOpen('profile')}
          openProfileImageChangeModal={() => handleOpen('profileImageChange')}
        />
      </Box>
      <OnBoardingModal rootId={DEFAULT_PORTAL_ROOT_ID} />
      <ProfileEditModal
        rootId={DEFAULT_PORTAL_ROOT_ID}
        isOpen={isOpen.profile}
        onClose={() => handleClose('profile')}
      />
      <ProfileImageChangeModal
        rootId={DEFAULT_PORTAL_ROOT_ID}
        isOpen={isOpen.profileImageChange}
        onClose={() => handleClose('profileImageChange')}
      />
      <CollectionCreateModal
        rootId={DEFAULT_PORTAL_ROOT_ID}
        articleId={articleId}
        isOpen={isOpen.collectionCreate}
        onClose={() => handleClose('collectionCreate')}
      />
    </UIOverlayContext.Provider>
  );
};

export default UIOverlayProvider;
